import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { type Shade, orderedSystems, systemLabel } from "./shade-groups";

const inputCls =
  "h-11 w-full rounded-md border border-zinc-200 bg-white px-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:h-9 sm:text-sm dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

const NOVO_SISTEMA = "__novo__";

/**
 * Adicionar cores ao catálogo — o espelho do fluxo de materiais: escolhe uma escala existente OU
 * cria uma nova pelo nome (fabricante/sistema). A diferença é que cor nasce em lote por natureza
 * (uma escala são dezenas de códigos), então o campo aceita vários de uma vez, separados por
 * vírgula, espaço ou quebra de linha, com prévia antes de confirmar.
 */
export function ShadeOptionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.catalog.myShadeOptions.useQuery(undefined, { enabled: open });
  const sistemas = useMemo(() => orderedSystems((listQuery.data ?? []) as Shade[]), [listQuery.data]);

  const [sistemaSel, setSistemaSel] = useState<string | null>(null);
  const [novoSistema, setNovoSistema] = useState("");
  const [codigosTexto, setCodigosTexto] = useState("");

  // null = ainda não escolheu nada: cai na primeira escala existente, ou em "nova" num catálogo vazio.
  const sistemaAtual = sistemaSel ?? sistemas[0] ?? NOVO_SISTEMA;
  const criandoSistema = sistemaAtual === NOVO_SISTEMA;

  const codigos = useMemo(
    () => [...new Set(codigosTexto.split(/[,;\s]+/).map((c) => c.trim()).filter(Boolean))],
    [codigosTexto],
  );

  const create = trpc.prosthesis.catalog.createCustomShadeOptions.useMutation({
    onSuccess: ({ created, skipped }) => {
      if (created === 0) toast.info("Nenhuma cor nova — todas já existiam nessa escala.");
      else
        toast.success(
          `${created} cor${created > 1 ? "es" : ""} adicionada${created > 1 ? "s" : ""}${
            skipped > 0 ? `, ${skipped} já existia${skipped > 1 ? "m" : ""}` : ""
          }.`,
        );
      utils.prosthesis.catalog.myShadeOptions.invalidate();
      utils.prosthesis.catalog.shadeOptions.invalidate();
      reset();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Erro ao adicionar cores."),
  });

  function reset() {
    setSistemaSel(null);
    setNovoSistema("");
    setCodigosTexto("");
  }

  const podeEnviar =
    codigos.length > 0 && codigos.length <= 100 && (!criandoSistema || novoSistema.trim().length >= 2);

  const enviar = () =>
    create.mutate({
      system: criandoSistema ? novoSistema.trim() : sistemaAtual,
      codes: codigos,
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[88dvh] max-w-md overflow-y-auto dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_CATALOGO.adicionarCores}>Adicionar cores</DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            Escolha uma escala ou crie uma nova e cole vários códigos de uma vez.
          </DialogDescription>
        </DialogHeader>

        <div>
          <p className={labelCls}>Fabricante / sistema</p>
          <select value={sistemaAtual} onChange={(e) => setSistemaSel(e.target.value)} className={inputCls}>
            {sistemas.map((s) => (
              <option key={s} value={s}>
                {systemLabel(s)}
              </option>
            ))}
            <option value={NOVO_SISTEMA}>+ Novo sistema…</option>
          </select>
        </div>

        {criandoSistema && (
          <div>
            <p className={labelCls}>Nome do novo sistema</p>
            <input
              value={novoSistema}
              onChange={(e) => setNovoSistema(e.target.value)}
              placeholder="Ex: Ivoclar Chromascop BL"
              autoFocus
              className={inputCls}
            />
          </div>
        )}

        <div>
          <p className={labelCls}>Códigos — separados por vírgula, espaço ou linha</p>
          <textarea
            value={codigosTexto}
            onChange={(e) => setCodigosTexto(e.target.value)}
            placeholder={"Ex: BL1, BL2, BL3, BL4"}
            rows={2}
            className="min-h-[44px] w-full resize-y rounded-md border border-zinc-200 bg-white px-3 py-2 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:text-sm dark:border-border dark:bg-card dark:text-foreground"
          />
          {codigos.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {codigos.slice(0, 30).map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:bg-primary/15 dark:text-primary"
                >
                  {c}
                </span>
              ))}
              {codigos.length > 30 && (
                <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  +{codigos.length - 30}
                </span>
              )}
              <span className="ml-auto text-[11px] font-medium text-zinc-500 dark:text-muted-foreground">
                {codigos.length > 100 ? "Máximo de 100 por vez" : `${codigos.length} de uma vez`}
              </span>
            </div>
          )}
        </div>

        <p className="rounded-md bg-zinc-50 px-2.5 py-2 text-[11px] leading-snug text-zinc-500 dark:bg-muted/40 dark:text-muted-foreground">
          Em que materiais cada escala existe se define ao criar ou editar o serviço, junto dos
          materiais que ele aceita.
        </p>

        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={create.isPending || !podeEnviar}
            onClick={enviar}
            className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50 sm:h-9 sm:flex-none"
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {codigos.length > 1 ? `Adicionar ${codigos.length} cores` : "Adicionar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
